import {
  FormControl,
  Select,
  FormLabel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  Link,
  Icon,
  Box,
  useColorModeValue,
  PopoverHeader,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  VStack,
} from '@ecoinvent/ui';
import { capitalize } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { BiChevronDown } from 'react-icons/bi';
import { FiExternalLink, FiHelpCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router';

import { RelatedDataset } from 'hooks/data/datasets/types';
import { systemModelSubtitles, systemModelDisplayNames } from 'pages/Dataset/components/RelatedDatasets/dropdownData';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

type Props = {
  label: string;
  options: RelatedDataset[];
  url_path: string;
  tooltip?: string;
  linkText?: string;
  linkHref?: string;
  useCustomDropdown?: boolean;
};

const RelatedDropdown = ({ label, options, url_path, tooltip, linkText, linkHref, useCustomDropdown }: Props) => {
  const navigate = useNavigate();
  const { increment } = useDatasetSession();
  const iconColor = useColorModeValue('green.500', 'green.300');
  const menuBg = useColorModeValue('white', 'gray.700');
  const menuHoverBg = useColorModeValue('gray.50', 'gray.700');
  const menuButtonHoverBg = useColorModeValue('gray.50', 'gray.600');
  const menuButtonBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const menuButtonTextColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const linkIconColor = useColorModeValue('green.500', 'green.200');

  const changeReleaseAndNavigate = ({ version, system_model, spold_id }: RelatedDataset) => {
    mixpanel.track(MIXPANEL_EVENTS.DATASET_VIEW_RELATED_DATASET, { version, system_model, spold_id, label });
    increment('view_related_dataset_count');
    navigate(`/${version}/${system_model}/dataset/${spold_id}/${url_path}`);
  };

  const getValue = (options: RelatedDataset[]) => {
    const obj = options.find((value: any) => value.is_current);
    return obj;
  };

  const currentValue = getValue(options);

  return (
    <FormControl size="sm" display="flex" flexDirection="column" flex={1}>
      <FormLabel fontSize={'sm'} htmlFor={`select-${label}`} id={`select-${label}`}>
        {label}
        {tooltip && (
          <Popover
            onOpen={() => {
              mixpanel.track(MIXPANEL_EVENTS.VIEW_RELATED_DROPDOWN_INFO_CLICKED, { label });
            }}
            onClose={() => {
              mixpanel.track(MIXPANEL_EVENTS.VIEW_RELATED_DROPDOWN_CLOSED, { label });
            }}
            strategy="fixed"
          >
            <PopoverTrigger>
              <Box as="span" verticalAlign={'middle'} ml={2} display={'inline-block'}>
                <Icon mt="2px" as={FiHelpCircle} color={iconColor} />
              </Box>
            </PopoverTrigger>
            <PopoverContent maxW="420px" w="auto" px={6} py={5}>
              <PopoverArrow />
              <PopoverCloseButton top={4} right={6} />
              <PopoverHeader borderBottom="none" p={0}>
                <Heading size="sm">{label}</Heading>
              </PopoverHeader>
              <PopoverBody p={0} mt={4}>
                {tooltip}
                {linkText && linkHref && (
                  <Link
                    href={linkHref}
                    isExternal
                    pt={4}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    onClick={() => {
                      mixpanel.track(MIXPANEL_EVENTS.CLICK_RELATED_DROPDOWN_INFO_LINK_CLICKED, { label, linkHref });
                    }}
                  >
                    <Icon as={FiExternalLink} color={linkIconColor} />
                    {linkText}
                  </Link>
                )}
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </FormLabel>
      <Box mt="auto">
        {useCustomDropdown ? (
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<Icon as={BiChevronDown} />}
              textAlign="left"
              fontWeight="normal"
              width="100%"
              disabled={options?.length < 1}
              color={menuButtonTextColor}
              borderColor={menuButtonBorder}
              borderWidth="1px"
              variant="outline"
              h="40px"
              fontSize="md"
              px="3"
              _hover={{ bg: menuHoverBg }}
              _active={{ bg: menuHoverBg }}
              _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 .5px var(--chakra-colors-green-500)' }}
              _expanded={{ borderColor: 'green.500', boxShadow: '0 0 0 .5px var(--chakra-colors-green-500)' }}
            >
              {currentValue?.system_model
                ? systemModelDisplayNames[currentValue.system_model] || capitalize(currentValue.system_model)
                : ''}
            </MenuButton>
            <MenuList bg={menuBg} maxW="680px" maxH="400px" overflowY="auto" py={0}>
              {options.map((item) => {
                const subtitle = systemModelSubtitles[item.system_model];
                const datasetName = item.description.split('-').slice(1).join('-').trim();
                const displayName =
                  capitalize(item.description) === systemModelDisplayNames[item.system_model] || !datasetName
                    ? systemModelDisplayNames[item.system_model] || capitalize(item.system_model)
                    : `${systemModelDisplayNames[item.system_model] || capitalize(item.system_model)} (${datasetName})`;
                return (
                  <MenuItem
                    key={item.system_model}
                    onClick={() => changeReleaseAndNavigate(item)}
                    bg="transparent"
                    _hover={{ bg: menuButtonHoverBg }}
                    py={3}
                  >
                    <VStack align="flex-start" spacing={0.5}>
                      <Text>{displayName}</Text>
                      {subtitle && (
                        <Text fontSize="xs" color="gray.500">
                          {subtitle}
                        </Text>
                      )}
                    </VStack>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        ) : (
          <Select
            disabled={options?.length <= 1}
            id={`${label}-select`}
            value={currentValue?.description}
            onChange={(e) => {
              const selectedOption = options.find((option) => option.description === e.target.value);
              if (!selectedOption) return;

              changeReleaseAndNavigate(selectedOption);
            }}
          >
            {options.map((item) => {
              return (
                <option key={`${item.description}`} value={item.description}>
                  {item.description}
                </option>
              );
            })}
          </Select>
        )}
      </Box>
    </FormControl>
  );
};

export default RelatedDropdown;
